import { Box, ButtonBase } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  minimizeDock,
  selectDockState,
  selectIsPanelOpen,
} from '../../state/aiSidekickSlice';
import { DOCK_STATE } from '../../state/types';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import MinimizeGlyph from '../icons/MinimizeGlyph';
import SidekickDotsIcon from '../icons/SidekickDotsIcon';
import {
  DOCK_BRAND_ICON_SIZE_PX,
  DOCK_TOGGLE_GLYPH_SIZE_PX,
} from './constants';
import ExpandedCard from './ExpandedCard';
import MinimizedBar from './MinimizedBar';
import useStyles from './SidekickDock.styles';
import type { SidekickDockProps } from './types';
import UpsellCard from './UpsellCard';

/**
 * Docked entry point for the Fleet AI Assistant, pinned to the host's
 * bottom-right corner and rendered outside the router so it survives
 * navigation.
 *
 * Yields to the drawer: both anchor to the same screen edge, so while
 * the panel is open the dock renders nothing rather than stacking
 * behind it.
 */
function SidekickDock({
  chips,
  isChipsLoading,
  hasTelematicsPlan,
  isTelematicsPlanLoading = false,
  showExplorePlansCta,
  onExplorePlans,
}: SidekickDockProps) {
  const { classes, cx } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const dispatch = useDispatch();
  const dockState = useSelector(selectDockState);
  const isPanelOpen = useSelector(selectIsPanelOpen);
  const isPlanReady = hasTelematicsPlan && !isTelematicsPlanLoading;

  const handleMinimize = useCallback(() => {
    dispatch(minimizeDock());
  }, [dispatch]);

  if (isPanelOpen) {
    return null;
  }

  if (dockState === DOCK_STATE.MINIMIZED) {
    return <MinimizedBar isPlanReady={isPlanReady} />;
  }

  return (
    <Box
      className={cx(classes.root, classes.card)}
      data-testid="ai-sidekick-dock-card"
    >
      <Box className={classes.cardHeader}>
        <SidekickDotsIcon
          state="standby"
          decorative
          size={DOCK_BRAND_ICON_SIZE_PX}
        />
        <ButtonBase
          className={classes.iconSlot}
          onClick={handleMinimize}
          aria-label={t('dock.minimizeAriaLabel')}
          aria-expanded
          data-testid="ai-sidekick-dock-minimize"
        >
          <MinimizeGlyph size={DOCK_TOGGLE_GLYPH_SIZE_PX} />
        </ButtonBase>
      </Box>
      {/* The upsell is an assertion that the account has no plan, so
      an unanswered check falls to the assistant's loading state
      rather than to the claim. */}
      {!hasTelematicsPlan && !isTelematicsPlanLoading ? (
        <UpsellCard
          showExplorePlansCta={showExplorePlansCta}
          onExplorePlans={onExplorePlans}
        />
      ) : (
        <ExpandedCard
          chips={chips}
          isChipsLoading={isChipsLoading}
          isTelematicsPlanLoading={isTelematicsPlanLoading}
        />
      )}
    </Box>
  );
}

export default SidekickDock;
